<template>
  <section
    data-cy="housing-row-section"
    :class="{ 'housing-compact-mode': isCompactMode }"
  >
    <EquipmentRow data-cy="housing-row-equipment-row">
      <template #left> </template>

      <template #default="{ toggle }">
        <AddRemoveButtons
          data-cy="housing-row-add-remove-buttons"
          :disable-add-above="disableAddAbove"
          :disable-add-below="disableAddBelow"
          :disable-remove="disableRemove"
          :hide-add-above="hideAddAbove"
          :hide-add-below="hideAddBelow"
          :hide-remove="hideRemove"
          @add-above="$emit('add-above')"
          @add-below="$emit('add-below')"
          @remove="$emit('remove', housing)"
        >
          <template #middle-content>
            <EquipmentPanel
              class="flex-grow-1"
              :rule-names="['housing_obsolete']"
              :xref-id="housing?.id"
              data-cy="housing-row-equipment-panel"
              @toggle="toggle"
            >
              <template #header>Housing</template>
              <template v-if="housing" #trigger>
                <span
                  v-if="housing?.r_housing"
                  data-cy="housing-row-equipment-panel-housing-label"
                >
                  {{ computeHousingLabel(housing.r_housing) }}
                </span>
              </template>
            </EquipmentPanel>
          </template>
        </AddRemoveButtons>
      </template>
      <template #expand="{ toggle }">
        <!-- Need to cast r_housings as Housings since they are coming from XrefPumps on the Sizings Query -->
        <HousingTable
          :is-admin="false"
          :pump-id="xrefPump?.pump_id"
          :pump-housings="xrefPump?.r_pump?.r_housings as Housings[]"
          data-cy="housing-row-housing-table"
          @select="handleHousingSelect(housing, $event, toggle)"
        />
      </template>
      <template #right-raw>
        <div v-if="isCompactMode" class="col" data-cy="housing-row-right-raw">
          <FormLabel
            v-slot="{ id }"
            text="Stages"
            data-cy="housing-row-formlabel-stages"
          >
            <InputNumber
              :id="id"
              :model-value="housing?.stage_count"
              :disabled="!housing?.r_housing"
              data-cy="housing-row-input-number"
              @update:model-value="
                $emit('update:housings', {
                  ...housing,
                  stage_count: $event
                })
              "
            />
          </FormLabel>
        </div>
        <div
          v-else
          class="housing-grid grid flex"
          data-cy="housing-row-right-raw"
        >
          <div class="col stages-field">
            <FormLabel
              v-slot="{ id }"
              text="Stages"
              data-cy="housing-row-formlabel-stages"
            >
              <InputNumber
                :id="id"
                :model-value="housing?.stage_count"
                :disabled="!housing?.r_housing"
                data-cy="housing-row-input-number"
                @update:model-value="
                  $emit('update:housings', {
                    ...housing,
                    stage_count: $event
                  })
                "
              />
            </FormLabel>
          </div>
          <div class="col">
            <ConverterInput
              class="flex-grow"
              label="Length"
              :model-value="housing?.length_inches"
              :target-unit="getLargeLengthUnit('Length.convert.Inches')"
              :base-unit="LengthUnits.Inches"
              :model="LargeLengthUnits.options()"
              mode="convert"
              data-cy="housing-row-converter-input"
              @update:model-value="
                $emit('update:housings', {
                  ...housing,
                  length_inches: $event
                })
              "
            />
          </div>
          <div class="col">
            <ConverterInput
              label="Weight"
              :model-value="housing?.r_housing?.weight_pounds"
              :base-unit="MassUnits.Pounds"
              :model="MassUnits.options()"
              mode="convert"
            />
          </div>
        </div>
      </template>
    </EquipmentRow>

    <div v-if="isCompactMode" class="housing-row-second flex">
      <div class="housing-length-compact flex-1">
        <ConverterInput
          class="flex-grow"
          label="Length"
          :model-value="housing?.length_inches"
          :target-unit="getLargeLengthUnit('Length.convert.Inches')"
          :base-unit="LengthUnits.Inches"
          :model="LargeLengthUnits.options()"
          mode="convert"
          data-cy="housing-row-converter-input"
          @update:model-value="
            $emit('update:housings', {
              ...housing,
              length_inches: $event
            })
          "
        />
      </div>
      <div class="housing-weight-compact flex-1">
        <ConverterInput
          label="Weight"
          :model-value="housing?.r_housing?.weight_pounds"
          :base-unit="MassUnits.Pounds"
          :model="MassUnits.options()"
          mode="convert"
        />
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import type { PropType } from "vue";
import { provide, computed } from "vue";
import { LengthUnits, LargeLengthUnits } from "@/libs/units/length";
import { MassUnits } from "@/libs/units/mass";
import { computeHousingLabel } from "@/models/equipment/housings/properties";
import ConverterInput from "@/components/ConverterInput.vue";
import EquipmentRow from "@/components/scenarios/EquipmentRow.vue";
import AddRemoveButtons from "@/components/AddRemoveButtons.vue";
import EquipmentPanel from "@/components/scenarios/EquipmentPanel.vue";
import FormLabel from "@/components/FormLabel.vue";
import HousingTable from "./HousingTable.vue";
import type { XrefHousings, XrefPump } from "@/composables/forms/formTypes";
import type { Housings } from "gql/api";
import { useScenarioUnits } from "@/composables/units/useScenarioUnits";
import {
  scenarioKey,
  sizingKey
} from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import { injectStrict } from "@/utils/utils";

const props = defineProps({
  housing: {
    type: Object as PropType<XrefHousings[number]>,
    default: null
  },
  housingIndex: {
    type: Number,
    default: undefined
  },
  xrefPump: {
    type: Object as PropType<XrefPump>,
    default: null
  },
  hideAddAbove: {
    type: Boolean,
    default: false
  },
  hideAddBelow: {
    type: Boolean,
    default: false
  },
  hideRemove: {
    type: Boolean,
    default: false
  },
  disableAddAbove: {
    type: Boolean,
    default: false
  },
  disableAddBelow: {
    type: Boolean,
    default: false
  },
  disableRemove: {
    type: Boolean,
    default: false
  },
  stack: {
    type: String as PropType<"upper" | "lower" | undefined>,
    default: undefined
  }
});

const $emit = defineEmits<{
  (e: "add-below"): void;
  (e: "add-above"): void;
  (e: "remove", housing: XrefHousings[number]): void;
  (e: "update:housings", housing: XrefHousings[number]): void;
}>();

provide(
  "selected-equipment-id",
  computed(() => props.housing?.r_housing?.id)
);

const scenario = injectStrict(scenarioKey);
const sizing = injectStrict(sizingKey, null);
const { getLargeLengthUnit } = useScenarioUnits(scenario, sizing);

// Computed for compact mode detection
const isCompactMode = computed(() => !!props.stack);

const handleHousingSelect = (
  xrefHousing: XrefHousings[number],
  selectedHousingEquipment: Housings,
  toggle: () => void
) => {
  toggle();
  $emit("update:housings", {
    ...xrefHousing,
    id: xrefHousing?.id,
    housing_id: selectedHousingEquipment.id,
    r_housing: selectedHousingEquipment,
    length_inches: selectedHousingEquipment.length_inches,
    stage_count: selectedHousingEquipment.stage_count
  });
};
</script>
<style lang="scss" scoped>
section[data-cy="housing-row-section"] {
  margin-bottom: 1rem;
}

.housing-grid.grid {
  .col.stages-field {
    flex: 0 0 110px !important;
    max-width: 110px !important;
    width: 110px !important;
    :deep(.fixed-inner-label) {
      input,
      .p-inputnumber > input {
        min-width: 80px !important;
        max-width: 110px !important;
        width: 100% !important;
      }
    }
    :deep(.p-inputnumber),
    :deep(.p-inputnumber-input) {
      max-width: 110px !important;
      width: 100% !important;
    }
  }
}

.housing-row-second {
  display: flex;
  width: 100%;
  gap: 1rem;
  margin-top: 0.25rem;

  .housing-length-compact,
  .housing-weight-compact {
    flex: 1 1 50%;
    min-width: 0;
    max-width: 50%;
  }
}
</style>
